// apps/client/src/audio/mixer.ts (title screen, 2026-10-05) — which song the page plays, and the crossfade between them: the title
// theme (audio/titleScore.ts) from Play now until a town is on screen, the town music (audio/score.ts) there, and back on the towns
// list. Pure (times are the audio clock's seconds), so audio/sound.ts only applies its fader moves to Web Audio gains.

export type Song = 'title' | 'town';
export const SONGS: readonly Song[] = ['title', 'town'];
/** A fader move: `song`'s gain goes linearly from `from` at `start` to `to` at `end`. */
export interface Fade { song: Song; from: number; to: number; start: number; end: number }

/** The music bus level for a music volume (settings.music, 0 = muted), a little lower indoors. Look-demo values. */
export function musicLevel(volume: number, indoor: boolean): number { return (indoor ? 0.26 : 0.32) * volume; }

export class MusicMixer {
  current: Song | null = null;
  private readonly fades = new Map<Song, Fade>();
  private readonly crossfade: number;
  /** `crossfade`: seconds for a full fade (silent to full, or back); a fade from part way takes its share of that. */
  constructor(crossfade: number) { this.crossfade = crossfade; }

  /** `song`'s fader at `now`. */
  level(song: Song, now: number): number {
    const f = this.fades.get(song);
    if (!f) return 0;
    if (now >= f.end) return f.to;
    if (now <= f.start) return f.from;
    return f.from + ((f.to - f.from) * (now - f.start)) / (f.end - f.start);
  }
  /** Fades `song` up and every other song down from wherever their faders are; null fades everything out. */
  play(song: Song | null, now: number): Fade[] {
    if (song === this.current) return [];
    this.current = song;
    const moves: Fade[] = [];
    for (const s of SONGS) {
      const from = this.level(s, now), to = s === song ? 1 : 0;
      if (from === to) continue;
      const f: Fade = { song: s, from, to, start: now, end: now + this.crossfade * Math.abs(to - from) };
      this.fades.set(s, f);
      moves.push(f);
    }
    return moves;
  }
  /** The songs whose notes must still be scheduled at `now`: the current one, and any other until it has faded to silence. */
  active(now: number): Song[] {
    return SONGS.filter((s) => s === this.current || this.level(s, now) > 0 || (this.fades.get(s)?.end ?? -Infinity) > now);
  }
  /** Whether bringing `song` up at `now` starts it from the top: only when it had gone silent (a song still fading out carries on). */
  restarts(song: Song, now: number): boolean { return this.level(song, now) === 0; }
}
